import React from 'react';
import { cn } from '../cn';

interface IGridProps {
  className?: string;
  innerRef?: React.MutableRefObject<HTMLDivElement>;
  style?: React.CSSProperties;
}
export type TGridItemType = 'basic' | 'extended' | 'wide' | 'full' | 'narrow';
interface IGridItemProps {
  className?: string;
  type?: TGridItemType;
  style?: React.CSSProperties;
}
interface IGridItemsProps extends IGridItemProps {
  items?: React.ReactNode[];
  layout?: 'half' | 'third' | 'full' | 'sidebar-content';
  style?: React.CSSProperties;
}

export const Grid: React.FC<React.PropsWithChildren<IGridProps>> = ({ children, style, innerRef, className }) => {
  return <div ref={innerRef} className={cn(
    'grid grid-cols-riac md:grid-cols-riac-md gap-grid-riac w-full mt-riac-grid',
    className
  )} style={style}>
    {children}
  </div>;
};

const gridRowTypeMap = {
  'narrow': 'col-riac-narrow',
  'basic': 'col-riac-basic',
  'extended': 'col-riac-extended',
  'wide': 'col-riac-wide',
  'full': 'col-riac-full',
};

const gridColTypeMap = {
  'narrow': {
    'full': ['col-riac-narrow'],
    'half': ['col-riac-narrow-half-1 md:col-riac-narrow-half-1-md', 'col-riac-narrow-half-2 md:col-riac-narrow-half-2-md'],
    'third': null,
    'sidebar-content': null,
  },
  'basic': {
    'full': ['col-riac-basic'],
    'half': ['col-riac-basic-half-1 md:col-riac-basic-half-1', 'col-riac-basic-half-2 md:col-riac-basic-half-2'],
    'third': ['col-riac-basic-third-1 lg:col-riac-basic-third-1-lg', 'col-riac-basic-third-2 lg:col-riac-basic-third-2-lg', 'col-riac-basic-third-3 lg:col-riac-basic-third-3-lg'],
    'sidebar-content': null,
  },
  'extended': {
    'full': ['col-riac-extended'],
    'half': ['col-riac-extended-half-1 md:col-riac-extended-half-1-md', 'col-riac-extended-half-2 md:col-riac-extended-half-2-md'],
    'third': null,
    'sidebar-content': null,
  },
  'wide': {
    'full': ['col-riac-wide'],
    'half': ['col-riac-wide-half-1 md:col-riac-wide-half-1-md', 'col-riac-wide-half-2 md:col-riac-wide-half-2-md'],
    'sidebar-content': ['col-riac-wide-sidebar md:col-riac-wide-sidebar-md', 'col-riac-wide-content md:col-riac-wide-content-md'],
    'third': null,
  },
  'full': {
    'full': ['col-riac-full'],
    'half': null,
    'third': null,
    'sidebar-content': null,
  },
};

export const GridItem: React.FC<React.PropsWithChildren<IGridItemProps>> = ({ children, style, className, type = 'basic' }) => {
  return <div style={style} className={cn(
    gridRowTypeMap[type],
    className
  )}>
    {children}
  </div>;
};

export const GridItems: React.FC<IGridItemsProps> = ({ items, className, type = 'basic', layout = 'full' }) => {
  const colInfo = gridColTypeMap[type][layout];
  if(colInfo === null) {
    throw new Error(`Layout ${layout} is not supported for type ${type}`);
  }
  return items?.map((item, index) => {
    if(item && item.props.className && item.props.className.includes('hidden')) {
      if(typeof className === 'undefined') {
        className = '';
      }
      className += ' hidden';
    }
    return (<div key={index} className={cn(
      gridColTypeMap[type][layout]![index % gridColTypeMap[type][layout]!.length],
      className,
    )}>
      {item}
    </div>);
  });
};
